import { DatePreset, DateRange } from '../uikit';

// COM-639 Task 3: pure filter-state logic for AllArticlesPage's Sources/Date
// toolbar filters - the `active`-prop question the kit's `FilterButton`/
// `DateRangePicker` left to their callers (see DateRangePicker.tsx's own
// comment on its `active` prop) is answered here, once, for both triggers.
//
// The load-bearing decision (docs/superpowers/specs/2026-08-17-com639-filters-design.md):
// "default" is NOT "every source individually selected" - it is "no
// `sourceIds` sent at all". Storing "everything selected" as an explicit id
// list would silently exclude a source added tomorrow from a filter the
// user never touched (see the spec's own reasoning). So `sourceIds: null`
// is the one and only "no filter" value; a concrete array - even one that
// happens to list every currently-active source - is a real user selection
// that is compared, by SET, against whatever `fetchFilterSources` actually
// returned (`activeSourceIds` below, threaded down from AllArticlesPage's
// own cache of the Sources dropdown's load - see SourcesFilter.tsx). When
// that comparison finds the two sets equal, the filter reads as equivalent
// to default: no highlight, no `sourceIds` param sent - exactly as if the
// user had never touched the control at all.

export interface FeedFiltersState {
  /** `null` = default (no filter, "show everything"). A concrete array -
   * INCLUDING one that happens to list every active source, or `[]` - is a
   * real user selection; see `isAllSourcesSelected` for how "every active
   * source individually checked" collapses back to default anyway. */
  sourceIds: string[] | null;
  datePreset: DatePreset;
  dateRange: DateRange;
}

export const DEFAULT_FEED_FILTERS: FeedFiltersState = {
  sourceIds: null,
  datePreset: 'custom',
  dateRange: { from: null, to: null },
};

/** True when `sourceIds` is either `null` (the explicit default) or a
 * concrete array whose SET of ids exactly matches `activeSourceIds`
 * (order-independent) - the "user individually re-checked every box" case,
 * which must read as "all selected" exactly like `null` does. Both
 * `SourcesFilter` (its Select all/Deselect all toggle label) and
 * `sourcesFilterActive` below (the `active`-prop / no-param decision) go
 * through this single function so the two can never disagree about what
 * "all selected" means. */
export function isAllSourcesSelected(sourceIds: string[] | null, activeSourceIds: string[]): boolean {
  if (sourceIds === null) return true;
  if (sourceIds.length !== activeSourceIds.length) return false;
  const selected = new Set(sourceIds);
  return activeSourceIds.every((id) => selected.has(id));
}

/** Drives the Sources `FilterButton`'s `active` prop. */
export function sourcesFilterActive(filters: FeedFiltersState, activeSourceIds: string[]): boolean {
  return !isAllSourcesSelected(filters.sourceIds, activeSourceIds);
}

/**
 * Whole-branch review fix wave (Important 4): decides whether a newly grown
 * active-source list (a source finished adding, gaining a checkbox in the
 * Sources dropdown - `SourcesFilter`'s `onSourcesLoaded`, AllArticlesPage.tsx)
 * should reset `sourceIds` back to the canonical `null` ("all selected")
 * rather than leaving it pointing at a now-stale concrete array.
 *
 * Reachable narrowly but for real: "Select all" sets `sourceIds` to `null`,
 * but unchecking one box then re-checking it leaves a CONCRETE array
 * instead - one that happens to SET-equal `previousActiveSourceIds`, which
 * `isAllSourcesSelected` also reads as "all selected". While that holds,
 * `sourcesFilterActive` is false and a catch-up refetch correctly sends no
 * `sourceIds` param, so the newly added source's articles already appear -
 * but the STALE array no longer set-equals the GROWN active list once the
 * new source joins it, so `sourcesFilterActive` flips true with no action
 * from the user: the Sources button lights up as "filtered" and the new
 * source's own checkbox renders unchecked, while its articles are already
 * visible in the grid.
 *
 * Collapses to `null` (not a grown array containing the new id) ONLY when
 * `sourceIds` was already equivalent to "all selected" against the OLD list
 * - a genuine deviation (the user deliberately excluded some sources) was
 * never "all selected" against the old list either, so it passes through
 * unchanged: a newly added source must not silently join an intentional
 * subset filter the user set up on purpose. `null`, not a grown concrete
 * array: both read identically through `isAllSourcesSelected`, and `null`
 * is the form every other "all selected" path in this codebase already
 * produces (the "Select all" toggle, SourcesFilter.tsx) - no second
 * representation of the same state is needed downstream.
 *
 * Returns the ORIGINAL `sourceIds` reference unchanged whenever nothing
 * needs to change, so a caller storing the result back into state (a
 * `setFilters` functional update) can skip triggering a re-render/refetch
 * for the common case where this is a no-op.
 */
export function reconcileSourceIdsOnSourcesGrowth(
  sourceIds: string[] | null,
  previousActiveSourceIds: string[]
): string[] | null {
  if (sourceIds !== null && isAllSourcesSelected(sourceIds, previousActiveSourceIds)) {
    return null;
  }
  return sourceIds;
}

/** Drives the `DateRangePicker`'s `active` prop. This page's own default
 * preset is `'custom'` with an empty range (see `DEFAULT_FEED_FILTERS`) -
 * deliberately NOT relying on `DateRangePicker`'s own naive default
 * calculation (its doc comment explicitly says a page with a non-`custom`
 * default should compute its own); this happens to reduce to the same
 * expression only because this page's default preset IS `'custom'`. */
export function dateFilterActive(filters: FeedFiltersState): boolean {
  return filters.datePreset !== 'custom' || Boolean(filters.dateRange.from || filters.dateRange.to);
}

/** True exactly when NEITHER filter deviates from default - the switch
 * between the ordinary "ready" grid and the "No results found"/Clear
 * filters empty state on a zero-row reply (AllArticlesPage.tsx). */
export function isDefaultFilters(filters: FeedFiltersState, activeSourceIds: string[]): boolean {
  return !sourcesFilterActive(filters, activeSourceIds) && !dateFilterActive(filters);
}

// UTC calendar day, matching how `resolvePreset` (uikit/lib/dateRangePresets.ts)
// anchors its Date objects and how the backend stores/compares dates (see
// allArticlesFeed.ts's `formatFeedArticleDate` for the same UTC-getter
// pattern applied to the read side).
//
// Known caveat (out of Task 3's file scope - DateRangePicker.tsx is a kit
// component from an earlier task): the kit's OWN custom-range `DatePicker`
// edges (`setCustomEdge` in DateRangePicker.tsx) build their `Date` via a
// plain (non-`.utc()`) `dayjs`, i.e. anchored at LOCAL midnight, unlike
// `resolvePreset`'s explicit UTC midnight. A bare `Date` carries no record
// of which anchor produced it, so no single extraction here can be exactly
// correct for both paths at once for every timezone - for a positive
// UTC-offset viewer (ahead of UTC, e.g. most of Europe) manually picking a
// custom "from"/"to" date, this can read back one calendar day earlier than
// what they clicked. UTC extraction is still the right default here: it is
// exactly correct for the preset path (the primary, e2e-covered flow) and
// matches the backend's confirmed UTC session (Task 1 report) - flagged for
// the designer/DateRangePicker owner rather than patched here.
function toDateOnly(date: Date): string {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export interface FeedFilterParams {
  sourceIds?: string[];
  dateFrom?: string;
  dateTo?: string;
}

/** Converts filter state into the params `fetchArticlesFeed` understands
 * (frontend/src/lib/feedRequest.ts's `FeedRequestParams`). Default filters
 * produce an EMPTY object - no key at all, not even an empty array - so an
 * untouched page's request is indistinguishable from one made before this
 * feature existed. A deviation always sends a concrete `sourceIds` array
 * (possibly `[]`, the deliberate "deselect everything" signal the backend
 * preserves - see feedRequest.ts's `normalizeSourceIds`) - never `null`,
 * which server-side means "absent". */
export function toFeedParams(filters: FeedFiltersState, activeSourceIds: string[]): FeedFilterParams {
  const params: FeedFilterParams = {};

  if (sourcesFilterActive(filters, activeSourceIds)) {
    // Guaranteed non-null: `sourcesFilterActive` is only true when
    // `isAllSourcesSelected` is false, which is only reachable when
    // `filters.sourceIds` isn't `null` (see `isAllSourcesSelected`'s first
    // branch, which always returns true for `null`).
    params.sourceIds = filters.sourceIds as string[];
  }

  if (dateFilterActive(filters)) {
    if (filters.dateRange.from) params.dateFrom = toDateOnly(filters.dateRange.from);
    if (filters.dateRange.to) params.dateTo = toDateOnly(filters.dateRange.to);
  }

  return params;
}

/** Unnamed-source fallback (Figma 2871:52928's checkbox list): the link's
 * hostname. An unparsable url falls back to the raw string as-is rather
 * than throwing or rendering blank - a source with a malformed url is
 * exactly the case where showing SOMETHING (even the raw string) beats
 * showing nothing. */
export function urlDomain(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

/** Sources filter checkbox label: the source's own name, or (when blank/
 * whitespace-only) its link's domain. */
export function sourceDisplayName(source: { name: string; url: string }): string {
  const trimmed = source.name.trim();
  return trimmed.length > 0 ? trimmed : urlDomain(source.url);
}
